import { motion, useReducedMotion } from "framer-motion";
import { useConfig } from "../context/useConfig";
import { fadeInUp, fadeIn } from "../utils/motionVariants";

function Contact() {
  const { config } = useConfig();
  const reduceMotion = useReducedMotion();

  const heading = config.contactSection?.heading || 'Let\'s Connect';
  const intro = config.contactSection?.intro || "I'm always open to discussing new opportunities, collaborations, or interesting projects.";
  const linkedinConfig = config.contactSection?.linkedin || {};

  return (
    <section id="contact" aria-labelledby="contact-heading" className="relative isolate py-28 sm:py-32 bg-gradient-to-b from-emerald-50 via-white to-cyan-50 overflow-hidden">
      {/* Background decorations */}
      <div aria-hidden className="hidden sm:block absolute -top-24 -left-24 w-[30rem] h-[30rem] rounded-full bg-emerald-300/40 blur-3xl" />
      <div aria-hidden className="hidden sm:block absolute bottom-[-18%] -right-24 w-[32rem] h-[32rem] rounded-full bg-cyan-300/40 blur-3xl" />
      <div aria-hidden className="absolute inset-0 pattern-dots" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} className="space-y-20">

          {/* Header */}
          <header className="max-w-3xl mx-auto text-center">
            <motion.h2
              id="contact-heading"
              variants={fadeInUp(0, reduceMotion ? 0 : 24)}
              className="text-4xl sm:text-5xl font-extrabold tracking-tight gradient-text"
            >
              {heading}
            </motion.h2>
            <motion.p
              variants={fadeIn(0.15)}
              className="mt-6 text-lg text-gray-600 font-medium leading-relaxed"
            >
              {intro}
            </motion.p>
          </header>

          {/* LinkedIn CTA Card - Centered */}
          <div className="max-w-2xl mx-auto">
            <motion.div
              variants={fadeInUp(0.1, reduceMotion ? 0 : 24)}
              className="relative overflow-hidden rounded-3xl bg-accent-gradient px-8 py-12 shadow-lg ring-1 ring-emerald-200/50 text-white text-center group"
            >
              {/* Gradient overlay on hover */}
              <div aria-hidden="true" className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.25),transparent_60%)]" />

              <div className="relative z-10 space-y-6">
                {/* LinkedIn Icon */}
                <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                  <i className="fab fa-linkedin text-4xl text-white" aria-hidden="true"></i>
                </div>

                {/* Heading */}
                <h3 className="text-3xl font-bold tracking-tight">
                  {linkedinConfig.heading || 'Connect on LinkedIn'}
                </h3>

                {/* Description */}
                <p className="text-base leading-relaxed text-indigo-100 max-w-lg mx-auto">
                  {linkedinConfig.description || 'The best way to reach me is through LinkedIn. Let\'s connect and discuss how we can work together.'}
                </p>

                {/* CTA Button */}
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href={config.contact.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 rounded-xl bg-white/95 px-8 py-4 text-base font-semibold tracking-wide text-indigo-700 shadow-lg hover:bg-white hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white transition-all"
                >
                  <i className={linkedinConfig.ctaIcon || "fab fa-linkedin"} aria-hidden="true"></i>
                  {linkedinConfig.ctaText || 'View LinkedIn Profile'}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </motion.a>
              </div>
            </motion.div>
          </div>

        </motion.div>
      </div>
    </section>
  );
}

export default Contact;
